import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from "react";
import uniAdminAuthService from "../services/university-admin/authService";
import { CLUB_SCOPED_KEYS } from "../utils/permissionKeys";

// Default value used when this hook is called outside a UniAdminAuthProvider
// (the public /university-manager mock-demo route tree, which shares most of
// this UI but never mounts real auth). `can` defaulting to true preserves
// that tree's existing "show everything" behavior; only the real /uni-admin
// tree gets actual gating.
const UniAdminAuthContext = createContext({
  can: () => true,
  isSuperAdmin: false,
  allClubsAccess: false,
  clubScopeIds: [],
  university: null,
  permissionsLoading: false,
});

export const useUniAdminAuth = () => {
  const context = useContext(UniAdminAuthContext);
  if (!context) {
    throw new Error("useUniAdminAuth must be used within a UniAdminAuthProvider");
  }
  return context;
};

const EMPTY_PERMISSIONS = { superAdmin: false, allClubsAccess: false, permissions: [], clubScopeIds: [], university: null };

// Don't refetch on every window focus — only if the last fetch is stale.
const REVALIDATE_INTERVAL_MS = 30000;

export const UniAdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [myPermissions, setMyPermissions] = useState(EMPTY_PERMISSIONS);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const lastFetchRef = useRef(0);

  const fetchPermissions = useCallback(async () => {
    lastFetchRef.current = Date.now();
    try {
      const response = await uniAdminAuthService.getMyPermissions();
      setMyPermissions(
        response.success
          ? {
              superAdmin: !!response.data.superAdmin,
              allClubsAccess: !!response.data.allClubsAccess,
              permissions: response.data.permissions || [],
              clubScopeIds: response.data.clubScopeIds || [],
              university: response.data.university || null,
            }
          : EMPTY_PERMISSIONS
      );
    } catch {
      // Fail closed: an admin whose permissions we couldn't load sees the
      // same UI as one with no grants, rather than a stale/optimistic one.
      setMyPermissions(EMPTY_PERMISSIONS);
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  useEffect(() => {
    try {
      const token = uniAdminAuthService.getToken();
      if (token) {
        const userData = uniAdminAuthService.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
        fetchPermissions();
      } else {
        setPermissionsLoading(false);
      }
    } catch (error) {
      console.error("UniAdmin auth initialization error:", error);
      setPermissionsLoading(false);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A Super Admin can change a Sub-Admin's grants at any time with no other
  // signal, so revalidate whenever the tab regains focus (throttled above).
  useEffect(() => {
    if (!isAuthenticated) return;
    const handleFocus = () => {
      if (Date.now() - lastFetchRef.current > REVALIDATE_INTERVAL_MS) {
        fetchPermissions();
      }
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [isAuthenticated, fetchPermissions]);

  const login = async (email, password, remember = false) => {
    try {
      const response = await uniAdminAuthService.login(email, password, remember);
      if (response.success) {
        const userData = response.data.user || { email };
        setUser(userData);
        setIsAuthenticated(true);
        setPermissionsLoading(true);
        fetchPermissions();
        return { success: true };
      }
      return { success: false, error: response.error || "Login failed" };
    } catch (error) {
      return { success: false, error: error.message || "Login failed" };
    }
  };

  const logout = () => {
    uniAdminAuthService.logout();
    setUser(null);
    setIsAuthenticated(false);
    setMyPermissions(EMPTY_PERMISSIONS);
    setPermissionsLoading(true);
  };

  const universityId = user?.universityId ?? null;

  // Mirrors the can() pseudocode from the permission-gating guide.
  const can = useCallback(
    (key, clubId) => {
      if (myPermissions.superAdmin) return true;
      if (!myPermissions.permissions.includes(key)) return false;
      if (CLUB_SCOPED_KEYS.has(key)) {
        return myPermissions.allClubsAccess || myPermissions.clubScopeIds.includes(clubId);
      }
      return true;
    },
    [myPermissions]
  );

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated,
    universityId,
    permissionsLoading,
    can,
    isSuperAdmin: myPermissions.superAdmin,
    allClubsAccess: myPermissions.allClubsAccess,
    clubScopeIds: myPermissions.clubScopeIds,
    university: myPermissions.university,
  };

  return (
    <UniAdminAuthContext.Provider value={value}>
      {children}
    </UniAdminAuthContext.Provider>
  );
};
